import { commesse as apiCommesse } from '../../api/commesse';
import type { CommessaAperta } from '../../api/commesse';

/*  Le commesse su cui l'operaio può segnare le ore, disponibili anche senza campo.

    Il rapportino è la parte dell'applicativo che deve funzionare in un cantiere senza
    rete, quindi l'elenco delle commesse non può arrivare da una chiamata fatta al
    momento: si scarica quando c'è linea e si tiene da parte. Senza cache e senza rete non
    si inventa niente — si dice che l'elenco non c'è, perché un elenco vuoto sembra
    «nessuna commessa aperta», che è un'altra cosa e manda l'operaio a cercare l'errore
    dove non è.

    localStorage e non IndexedDB: sono poche righe di testo, e la coda dei rapportini —
    quella sì da non perdere — sta già nel suo archivio in coda.ts.  */

const CHIAVE = 'teraworks.commesse-aperte';

export interface CommesseInCantiere {
  elenco: CommessaAperta[];
  /** Quando è stato scaricato l'elenco che si sta usando: null se è appena arrivato. */
  scaricatoIl: string | null;
  /** Vero quando si sta usando la copia salvata perché la rete non ha risposto. */
  dallaCache: boolean;
}

function leggiCache(): { elenco: CommessaAperta[]; scaricatoIl: string } | null {
  try {
    const salvato = localStorage.getItem(CHIAVE);
    if (!salvato) return null;

    const letto = JSON.parse(salvato) as { elenco?: unknown; scaricatoIl?: unknown };
    if (!Array.isArray(letto.elenco) || typeof letto.scaricatoIl !== 'string') return null;

    return { elenco: letto.elenco as CommessaAperta[], scaricatoIl: letto.scaricatoIl };
  } catch {
    // Archivio pieno, disabilitato o contenuto di una versione precedente: si riparte
    // dalla rete invece di far fallire la schermata.
    return null;
  }
}

function scriviCache(elenco: CommessaAperta[]) {
  try {
    localStorage.setItem(CHIAVE, JSON.stringify({ elenco, scaricatoIl: new Date().toISOString() }));
  } catch {
    // Non poter salvare la copia non è un errore da mostrare: il rapportino di adesso
    // si compila comunque, sarà il prossimo avvio senza rete a non trovare l'elenco.
  }
}

export async function commesseInCantiere(): Promise<CommesseInCantiere> {
  try {
    const elenco = await apiCommesse.aperte();
    scriviCache(elenco);
    return { elenco, scaricatoIl: null, dallaCache: false };
  } catch {
    const cache = leggiCache();
    if (cache === null) throw new Error('Elenco commesse non disponibile');

    return { elenco: cache.elenco, scaricatoIl: cache.scaricatoIl, dallaCache: true };
  }
}
